import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { usePagination } from '@adornis/pagination/use-pagination.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { Group } from '../../groups/entities/Group.js';
import { BillingPositionTemplateAPI } from '../api/BillingPositionTemplateAPI.js';
import { translateBillingRhythm } from '../entities/BillingPosition.js';
import { BillingPositionTemplate } from '../entities/BillingPositionTemplate.js';
import { BillingPositionTemplateEditDialog } from './BillingPositionTemplateEditDialog.js';

const SGBillingPositionTemplateEditDialogContent = stateful(
  (props: {
    scopes?: string[];
    resolve: (billingPosition: BillingPositionTemplate | null) => void;
    reject: () => void;
  }) => {
    const [filter] = useState(
      new AdornisFilter({
        searchFields: ['name', 'kuerzel'],
      }),
    );
    const [search, setSearch] = useState<string | null>(null);

    const pagination = usePagination<BillingPositionTemplate, [AdornisFilter]>({
      // @ts-expect-error narrowed entity
      queryOrSubscription: BillingPositionTemplateAPI.subscribe,
      args: [filter],
      selectionSet: selectionSet(() => BillingPositionTemplate, 1),
      initialPagination: 30,
      paginationStep: 10,
    });

    const { value: scopeResolved } = useQuery(() => subscribeByID(Group, props.scopes ?? [])({ _id: 1, name: 1 }), []);

    useEffect(() => {
      const newFilter = filter;

      newFilter.search = search;

      // filter by scopes
      if (props.scopes?.length) {
        newFilter.setFilterAspect(new ListAspect({ customID: 'scope', values: props.scopes }));
      }

      pagination.updateParameters(newFilter);
    }, [filter, search, props.scopes]);

    return html`
      <div class="dialog-container">
        ${DialogHeader({ title: 'Template auswählen', close: props.reject, closeLabel: 'Dialog schließen' })}

        <p>
          Hier können Sie ein Template auswählen, das Sie verwenden möchten. Die Werte werden dann in die
          Abrechnungsposition übertragen.
        </p>
        <div class="flex flex-row gap-3 items-center">
          ${TextInput({
            placeholder: 'Suche...',
            placeholderMode: 'hide',
            value: search,
            onChanged: value => setSearch(value),
          })}

          <button
            class="btn btn-success btn-square"
            @click=${() => BillingPositionTemplateEditDialog(null, props.scopes)}
          >
            <span class="icon-[material-symbols--add-rounded] size-5"></span>
          </button>
        </div>
        <div
          class="martinus-selection-backdrop grid grid-cols-[minmax(5rem,max-content)_minmax(6rem,max-content)_1fr_max-content_max-content] gap-2"
        >
          ${
            pagination.data?.length
              ? repeat(
                  pagination.data ?? [],
                  template => template._id,
                  template =>
                    html`<div
                      @click=${() => props.resolve(template as BillingPositionTemplate)}
                      class="martinus-selection-card"
                    >
                      <span class="badge badge-ghost badge-sm">
                        ${
                          props.scopes?.includes(template.scope ?? '')
                            ? scopeResolved?.find(group => group._id === template.scope)?.name
                            : 'global'
                        }
                      </span>
                      <p class="font-semibold whitespace-nowrap">${template.kuerzel}</p>
                      <p class="truncate">${template.name}</p>
                      <p class="text-base-content/70 whitespace-nowrap">
                        ${template.priceInCents ? `${(template.priceInCents / 100).toFixed(2)} €` : 'Kein Preis'}
                      </p>
                      <p class="text-base-content/70 text-sm whitespace-nowrap">
                        ${template.rhythm ? translateBillingRhythm(template.rhythm) : 'Kein Rhythmus'}
                      </p>
                    </div>`,
                )
              : html`<div class="col-span-full alert alert-info">Keine applizierbaren Templates gefunden</div>`
          }
        </div>
      </div>
    `;
  },
);

export const SGBillingPositionSelectionDialog = async ({ scopes }: { scopes?: string[] }) => {
  const selected = await showDialog<BillingPositionTemplate | null>({
    content: (resolve, reject) => SGBillingPositionTemplateEditDialogContent({ scopes, resolve, reject }),
    modalBoxClasses: 'max-w-[60vw]',
  });

  return selected;
};
